import { useGetConstants } from 'shared/utils/selectors';
import { useInvoiceFormContext } from './InvoiceForm.context';
import { isNoTva } from './InvoiceForm.utils';

const lineSubtotalHtva = (quantity: number, unitPrice: number, discountRate: number) =>
  quantity * unitPrice * (1 - discountRate / 100);

export const useInvoiceFormTotals = () => {
  const form = useInvoiceFormContext();
  const constants = useGetConstants();

  const productLines = form.values.product_lines_attributes;
  const debtor = form.values.debtor_attributes;
  const noTva = isNoTva(debtor);

  let totalHtva = 0;
  let totalHtvaNoDiscount = 0;
  let totalTvac = 0;
  let totalVat = 0;

  if (productLines == null) {
    return {
      totalTvac,
      totalHtva,
      totalVat,
      totalDiscount: totalHtvaNoDiscount - totalHtva,
    };
  }

  productLines.forEach(line => {
    if (!line) return;

    // Invoice-linked lines (credit notes pointing at an existing invoice) carry
    // explicit totals — those aren't derived from quantity/price.
    if (line.isInvoice) {
      totalHtva += Number(line.total_htva ?? 0);
      totalTvac += Number(line.total_tvac ?? 0);
      totalVat += Number(line.total_tvac ?? 0) - Number(line.total_htva ?? 0);
      return;
    }

    const quantity = Number(line.quantity ?? 0);
    const product = line.product_attributes ?? {};
    const price = Number(product.current_unity_price_htva ?? 0);
    const discountRate = Number(line.discount_rate ?? 0);
    const tvaCode = product.tax_category_code;
    const tvaRate = noTva
      ? 0
      : Number(constants.tax_categories.find(c => c.value === tvaCode)?.vat_value ?? 0);

    const subtotal = lineSubtotalHtva(quantity, price, discountRate);

    totalHtvaNoDiscount += price * quantity;
    totalHtva += subtotal;
    totalTvac += subtotal * (1 + tvaRate / 100);
    totalVat += subtotal * (tvaRate / 100);
  });

  return {
    totalTvac,
    totalHtva,
    totalVat,
    totalDiscount: totalHtvaNoDiscount - totalHtva,
  };
};
